import { ButcherHeader } from '@components/ButcherHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { useEffect, useState } from 'react';
import {
  CustomerDetailsCrumbSteps,
  errorMessage,
  responseTexts,
} from '@helpers/Constants';
import { useNavigate, useParams } from 'react-router-dom';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Customer, HuntingLicense, License, State } from '@helpers/Models';
import { useToast } from '@contexts/Toast';
import {
  formatDate,
  getFormattedPhoneNumber,
  handleNewOrderInitiation,
} from '@services/OrderCreationService';
import dayjs from 'dayjs';
import { AddOrEditLicense } from '@components/Modal/LicenseDetails/AddOrEditLicense';
import { useFetchStateName } from '@hooks/useFetchStateName';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TextField from '@mui/material/TextField';
import { SubscriptionValidation } from '@services/SubscriptionValidation';

const CustomerDetails: React.FC = () => {
  const navigate = useNavigate();
  const countryCode = '+1';
  const { getStateName } = useFetchStateName();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const { customerId } = useParams<{ customerId: string }>();
  const { showToastMessage } = useToast();
  const [showAddLicense, setShowAddLicense] = useState(false);
  const [showEditLicense, setShowEditLicense] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);

  const [licenses, setLicenses] = useState<License[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [editingLicenseIndex, setEditingLicenseIndex] = useState<number | null>(
    null,
  );
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    harvestStateId: '',
    licenseNumber: '',
    expiryDate: '',
  });
  const [isSubscriptionActive, setIsSubscriptionActive] =
    useState<boolean>(false);

  const normalizeToString = (value: string | null | undefined): string => {
    return value?.trim() || '';
  };

  const displayOrDash = (value: string | null | undefined): string => {
    return normalizeToString(value) || '-';
  };

  /**
   * Effect hook to fetch customer details when the component mounts or when the customer ID changes.
   *
   * This effect calls the `getCustomerDetails` function to retrieve the customer details
   * from the server based on the `customerId` parameter from the URL.
   */
  useEffect(() => {
    getCustomerDetails();

    // Verify the subscription status
    setIsSubscriptionActive(SubscriptionValidation());
  }, [customerId]);

  /**
   * Effect hook to synchronize licenses from customer data.
   *
   * This effect runs when the `customer` state changes. It maps the `HuntingLicences`
   * from the customer data to the `licenses` state, transforming the data to match
   * the `License` interface structure.
   */
  useEffect(() => {
    if (customer?.HuntingLicences) {
      setLicenses(
        customer.HuntingLicences.map((l) => ({
          id: l.id ?? -1,
          harvestStateId: l.harvestStateId ?? 0,
          firstName: customer.firstName ?? '',
          lastName: customer.lastName ?? '',
          licenseNumber: l.licenseNumber ?? '',
          expiryDate: l.expiryDate
            ? dayjs(l.expiryDate).format('MM/DD/YYYY')
            : '',
          HarvestState: l.HarvestState,
        })),
      );
    }
  }, [customer]);

  /**
   * Effect hook to fetch states once when the component mounts.
   *
   * This effect makes an API call to retrieve all states from the server
   * and updates the `states` state with the fetched data. It uses the `showToastMessage`
   * function to display an error message if the fetch operation fails.
   */
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await Axios.get(ApiEndpoints.FETCH_STATES);
        setStates(response.data.data);
      } catch {
        showToastMessage(errorMessage.FETCH_ERROR, 'error');
      }
    };
    fetchStates();
  }, []);

  /**
   * Fetches customer details from the server.
   *
   * This function makes an API call to retrieve customer details based on the `customerId`.
   * It updates the `customer` state with the fetched data and handles loading and error states.
   *
   * @returns {Promise<void>} A promise that resolves when the customer details have been fetched.
   */
  const getCustomerDetails = async () => {
    try {
      setLoading(true);
      const response = await Axios.post(ApiEndpoints.FETCH_CUSTOMERS, {
        id: Number(customerId),
      });
      if (response.data.data.customers.length > 0) {
        const fetchedCustomer = response.data.data.customers[0] as Customer;
        setCustomer({
          ...fetchedCustomer,
          phoneHome: normalizeToString(fetchedCustomer.phoneHome),
          primaryEmail: normalizeToString(fetchedCustomer.primaryEmail),
          address1: normalizeToString(fetchedCustomer.address1),
          address2: normalizeToString(fetchedCustomer.address2),
          address3: normalizeToString(fetchedCustomer.address3),
          city: fetchedCustomer.city || { id: 0, cityName: '' },
          postalCode: fetchedCustomer.postalCode || { id: 0, postalCode: '' },
        });
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles changes to a license field.
   *
   * This function updates the `licenses` state with the new value for the specified field
   * at the given index. It also clears any error message for that field.
   *
   * @param index - The index of the license to update.
   * @param field - The field of the license to update.
   * @param value - The new value for the field.
   */
  const handleLicenseChange = (
    index: number,
    field: keyof License,
    value: string | number,
  ) => {
    setLicenses((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  /**
   * Adds a new license to the licenses list.
   *
   * This function creates a new license object with default values and adds it to the `licenses` state.
   * It prevents adding a new license if there is already an unsaved license being edited.
   *
   * @returns {void}
   */
  const handleAddLicense = () => {
    if (editingLicenseIndex !== null) {
      showToastMessage(errorMessage.LICENSE_VALIDATION_ERROR, 'error');
      return;
    }
    setLicenses((prev) => [
      ...prev,
      {
        id: -1,
        harvestStateId: 0,
        firstName: customer?.firstName ?? '',
        lastName: customer?.lastName ?? '',
        licenseNumber: '',
        expiryDate: '',
      },
    ]);
    setEditingLicenseIndex(licenses.length);
  };

  /**
   * Sets the index of the license to be edited.
   *
   * @param index - The index of the license to edit.
   */
  const editLicense = (index: number) => setEditingLicenseIndex(index);

  /**
   * Cancels the editing of a license.
   *
   * If the license being cancelled is a new unsaved license (ID is -1), it removes it from the list.
   * It then clears the editing index and resets any validation errors.
   *
   * @param index - The index of the license to cancel editing for.
   */
  const cancelEdit = (index: number) => {
    // If new unsaved license, remove it
    if (licenses[index].id === -1) {
      setLicenses((prev) => prev.filter((_, i) => i !== index));
    }
    setEditingLicenseIndex(null);
    setErrors({
      firstName: '',
      lastName: '',
      harvestStateId: '',
      licenseNumber: '',
      expiryDate: '',
    });
  };

  /**
   * Validates the license form for a specific license.
   *
   * Checks if the first name, last name, harvest state, license number, and expiry date
   * are all filled. Updates the `errors` state with appropriate messages if any field is empty.
   *
   * @param index - The index of the license to validate.
   * @returns {boolean} True if the form is valid, false otherwise.
   */
  const validateForm = (index: number): boolean => {
    const l = licenses[index];
    const newErrors = {
      firstName: !l.firstName?.trim() ? 'First name is required' : '',
      lastName: !l.lastName?.trim() ? 'Last name is required' : '',
      harvestStateId: !l.harvestStateId ? 'State is required' : '',
      licenseNumber: !l.licenseNumber?.trim()
        ? 'License number is required'
        : '',
      expiryDate: !l.expiryDate?.trim() ? 'Expiry date is required' : '',
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((e) => e === '');
  };

  /**
   * Handles the saving of license details.
   *
   * This function is triggered when the user attempts to save license information.
   * It first validates the form, and if valid, it constructs a payload and makes an API call
   * to upsert the license details. It updates the `licenses` state with the saved data
   * and clears the editing state upon success.
   *
   * @param e - The form event.
   * @param index - The index of the license being saved.
   */
  const handleSaveLicenseDetails = async (
    e: React.FormEvent,
    index: number,
  ) => {
    e.preventDefault();
    if (!validateForm(index)) return;
    try {
      const licenseItem = licenses[index];
      const payload = {
        id: licenseItem.id ?? -1,
        harvestStateId: licenseItem.harvestStateId,
        firstName: licenseItem.firstName,
        lastName: licenseItem.lastName,
        licenseNumber: licenseItem.licenseNumber,
        expiryDate: dayjs(licenseItem.expiryDate).format('MM/DD/YYYY'),
      };
      const response = await Axios.post(ApiEndpoints.CUSTOMER_LICENSE_UPSERT, {
        customerId: customer?.id,
        license: payload,
      });

      if (response.data?.data?.license) {
        const savedLicense = response.data.data.license;
        const harvestState = states.find(
          (s) => s.id === savedLicense.harvestStateId,
        );
        const enriched = {
          ...savedLicense,
          HarvestState: harvestState
            ? { id: harvestState.id, stateName: harvestState.stateName }
            : null,
        };
        const updated = [...licenses];
        updated[index] = enriched;
        setLicenses(updated);
      }
      setEditingLicenseIndex(null);
      setErrors({
        firstName: '',
        lastName: '',
        harvestStateId: '',
        licenseNumber: '',
        expiryDate: '',
      });
      showToastMessage(responseTexts.LICENSE_CREATE_SUCCESS, 'success');
    } catch {
      showToastMessage(errorMessage.LICENSE_SAVE_FAILURE, 'error');
    }
  };

  /**
   * Handles the creation of a new order for the current customer.
   *
   * This function constructs a customer details object from the current `customer` state
   * and passes it to the `handleNewOrderInitiation` function, which handles the navigation
   * and initialization of the new order process.
   */
  const handleCreateNewOrder = (): void => {
    if (customer) {
      const customerDetails = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phoneHome: normalizeToString(customer.phoneHome),
        primaryEmail: normalizeToString(customer.primaryEmail),
        HuntingLicences: customer.HuntingLicences,
      };
      handleNewOrderInitiation(navigate, customerDetails);
    }
  };

  /**
   * Handles the navigation to the order history page for the current customer.
   *
   * This function is called when the user wants to view the past orders of the current customer.
   * It navigates to the `ORDER_HISTORY` route and passes the customer details in the state.
   *
   * @param customer - The customer object containing the details of the customer whose order history is to be viewed.
   */
  const handleViewPastOrders = (customer: {
    id: number;
    firstName: string;
    lastName: string;
    phoneHome: string;
    primaryEmail: string;
    HuntingLicences: HuntingLicense[];
  }) => {
    navigate(FrontendRoutes.ORDER_HISTORY, { state: customer });
  };

  if (loading) {
    return (
      <div className="flex h-screen w-screen gap-1">
        <SideNav active={2} />
        <div className="flex-1 p-1 overflow-auto relative">
          <div className="flex justify-center items-center h-full">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex h-screen w-screen gap-1">
        <SideNav active={2} />
        <div className="flex-1 p-1 overflow-auto relative">
          <div className="flex justify-center items-center h-full">
            <div>Customer not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={2} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        <OrderBreadcrumb
          steps={CustomerDetailsCrumbSteps}
          activeItem="Details"
          customerName={`${customer.firstName} ${customer.lastName}`}
          customerId={customer.id}
        />
        <div className="flex items-center justify-between mt-4 mb-4">
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-[#43474E] hover:text-primary text-lg mr-2 focus:outline-none"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <h2 className="text-lg font-semibold ml-2">Customer Details</h2>
          </div>
          <div className="flex mr-4 gap-3 items-center">
            <button
              type="button"
              className="text-primary font-bold"
              onClick={() => handleViewPastOrders(customer)}
            >
              Order History
            </button>
            <button
              disabled={isSubscriptionActive}
              onClick={handleCreateNewOrder}
              type="button"
              className={`px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white ${isSubscriptionActive ? 'cursor-not-allowed bg-[#00483480]' : 'bg-primary hover:bg-dark-primary'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary`}
            >
              New Order
            </button>
          </div>
        </div>

        {/* Basic Info Section */}
        <div className="border border-[#C5C9E2] rounded-md mx-4 mb-4">
          <div className="flex justify-between items-center px-4 py-3 border-b border-[#C5C9E2]">
            <span className="font-semibold text-base">Basic info</span>
            <button
              type="button"
              onClick={() =>
                navigate(
                  FrontendRoutes.UPDATE_CUSTOMER.replace(
                    ':customerId',
                    customer.id.toString(),
                  ),
                )
              }
              className="flex items-center gap-1 text-primary font-semibold text-sm"
            >
              <svg
                width="12"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0 translate-y-[1px]"
              >
                <path
                  d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                  fill="#004834"
                />
              </svg>
              Edit
            </button>
          </div>
          <div className="flex px-4 py-4 gap-6">
            {/* Left Column */}
            <div className="flex flex-col gap-4 w-1/2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#000A1666] mb-1">First Name</p>
                  <p className="font-medium">{customer.firstName}</p>
                </div>
                <div>
                  <p className="text-[#000A1666] mb-1">Last name</p>
                  <p className="font-medium">{customer.lastName}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#000A1666] mb-1">Email</p>
                  <p className="font-medium break-all">
                    {displayOrDash(customer.primaryEmail)}
                  </p>
                </div>
                <div>
                  <p className="text-[#000A1666] mb-1">Contact</p>
                  <p className="font-medium">
                    {customer.phoneHome
                      ? `${countryCode} ${getFormattedPhoneNumber(customer.phoneHome)}`
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-4 w-1/2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#000A1666] mb-1">Address line 1</p>
                  <p className="font-medium break-all">
                    {displayOrDash(customer.address1)}
                  </p>
                </div>
                <div>
                  <p className="text-[#000A1666] mb-1">Address line 2</p>
                  <p className="font-medium break-all">
                    {displayOrDash(customer.address2)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#000A1666] mb-1">Address line 3</p>
                  <p className="font-medium break-all">
                    {displayOrDash(customer.address3)}
                  </p>
                </div>
                <div>
                  <p className="text-[#000A1666] mb-1">Zip Code</p>
                  <p className="font-medium">
                    {displayOrDash(customer.postalCode?.postalCode)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[#000A1666] mb-1">State</p>
                  <p className="font-medium break-all">
                    {customer.stateId ? getStateName(customer.stateId) || '-' : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[#000A1666] mb-1">City</p>
                  <p className="font-medium">{displayOrDash(customer.city?.cityName)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* License Section */}
        <div className="border border-[#C5C9E2] rounded-md mx-4 mb-4">
          <div className="flex justify-between items-center px-4 py-3 border-b border-[#C5C9E2]">
            <span className="font-semibold text-base">License</span>
            <button
              type="button"
              onClick={handleAddLicense}
              className="flex items-center gap-1 px-4 py-1.5 border border-primary text-primary rounded-md font-semibold"
            >
              + Add License
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 p-4">
            {licenses.map((license, index) => {
              const isExpired = dayjs(license.expiryDate).isBefore(dayjs());
              const isEditing = editingLicenseIndex === index;

              return (
                <div
                  key={license.id ?? index}
                  className="border border-gray-200 rounded-md p-4"
                >
                  {isEditing ? (
                    // Edit/Add Form
                    <>
                      <div className="mb-3">
                        <p className="text-sm font-semibold">License Details</p>
                        <p className="text-xs text-gray-400">
                          (Please enter the details below as per the license)
                        </p>
                      </div>

                      {/* Row 1 */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-xs mb-1">First Name*</label>
                          <input
                            type="text"
                            value={license.firstName}
                            onChange={(e) =>
                              handleLicenseChange(index, 'firstName', e.target.value)
                            }
                            placeholder="First Name"
                            className={`w-full p-2 border rounded-md text-sm text-gray-600 focus:outline-primary ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                            {errors.firstName}
                          </p>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs mb-1">Last Name*</label>
                          <input
                            type="text"
                            value={license.lastName}
                            onChange={(e) =>
                              handleLicenseChange(index, 'lastName', e.target.value)
                            }
                            placeholder="Last Name"
                            className={`w-full p-2 border rounded-md text-sm text-gray-600 focus:outline-primary ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                            {errors.lastName}
                          </p>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs mb-1">State*</label>
                          <select
                            value={license.harvestStateId}
                            onChange={(e) =>
                              handleLicenseChange(
                                index,
                                'harvestStateId',
                                parseInt(e.target.value),
                              )
                            }
                            className={`w-full p-2 border rounded-md text-sm text-gray-600 focus:outline-primary ${errors.harvestStateId ? 'border-red-500' : 'border-gray-300'}`}
                          >
                            <option value={0}>Select state</option>
                            {states.map((state) => (
                              <option key={state.id} value={state.id}>
                                {state.stateName}
                              </option>
                            ))}
                          </select>
                          <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                            {errors.harvestStateId}
                          </p>
                        </div>
                      </div>

                      {/* Row 2 */}
                      <div className="flex gap-2 mb-3">
                        <div className="flex-1">
                          <label className="block text-xs mb-1">License number *</label>
                          <input
                            type="text"
                            value={license.licenseNumber}
                            onChange={(e) =>
                              handleLicenseChange(index, 'licenseNumber', e.target.value)
                            }
                            placeholder="License Number"
                            className={`w-full p-2 border rounded-md text-sm text-gray-600 focus:outline-primary ${errors.licenseNumber ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                            {errors.licenseNumber}
                          </p>
                        </div>
                        <div className="flex-1">
                          <label htmlFor="license.expiryDate" className="block text-xs mb-1">
                            License Expiry*
                          </label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              disablePast
                              format="MM/DD/YYYY"
                              value={
                                license.expiryDate ? dayjs(license.expiryDate) : null
                              }
                              onChange={(newValue) => {
                                if (newValue) {
                                  handleLicenseChange(
                                    index,
                                    'expiryDate',
                                    newValue.format('MM/DD/YYYY'),
                                  );
                                  setErrors((prev) => ({
                                    ...prev,
                                    expiryDate: '',
                                  }));
                                }
                              }}
                              desktopModeMediaQuery="@media (min-width: 0px)"
                              slots={{ textField: TextField }}
                              slotProps={{
                                textField: {
                                  placeholder: 'MM/DD/YYYY',
                                  size: 'small',
                                  error: !!errors.expiryDate,
                                  sx: {
                                    width: '100%',
                                    backgroundColor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      border: 'none',
                                    },
                                  },
                                  InputProps: {
                                    sx: {
                                      border: `1px solid ${errors.expiryDate ? '#ef4444' : '#d1d5db'}`,
                                      borderRadius: '0.375rem',
                                      fontSize: '0.875rem',
                                      '&.Mui-focused': {
                                        borderColor: '#240089',
                                      },
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                          <p className="text-red-500 text-xs mt-1 min-h-[16px]">
                            {errors.expiryDate}
                          </p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end items-center gap-6 mt-2 mb-2">
                        <button
                          type="button"
                          onClick={() => cancelEdit(index)}
                          className="text-primary font-bold text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleSaveLicenseDetails(e, index)}
                          className="bg-primary text-white px-6 py-2 rounded-md text-sm"
                        >
                          Save
                        </button>
                      </div>
                    </>
                  ) : (
                    // View mode
                    <>
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">License Details</span>
                          {isExpired && (
                            <span className="text-sm font-bold px-2 py-0.5 rounded-full border border-[#71758D] text-[#71758D] bg-[#E5E6EB]">
                              Expired
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => editLicense(index)}
                          className="flex items-center gap-1 text-primary font-semibold text-sm"
                        >
                          <svg
                            width="12"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="shrink-0 translate-y-[1px]"
                          >
                            <path
                              d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                              fill="#004834"
                            />
                          </svg>
                          Edit
                        </button>
                      </div>
                      {/* Grid with break-words to prevent overflow */}
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-1">First Name</p>
                          <p className="text-sm font-medium break-words">{license.firstName}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">Last name</p>
                          <p className="text-sm font-medium break-words">{license.lastName}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">State*</p>
                          <p className="text-sm font-medium break-words">
                            {getStateName(license.harvestStateId)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">License number *</p>
                          <p className="text-sm font-medium break-words">
                            {license.licenseNumber}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-1">License Expiry*</p>
                          <p className="text-sm font-medium break-words">
                            {formatDate(license.expiryDate)}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Add / Edit License Modal */}
        {(showAddLicense || showEditLicense) && (
          <AddOrEditLicense
            onClose={() => {
              setShowAddLicense(false);
              setShowEditLicense(false);
              setEditingLicense(null);
              getCustomerDetails(); // refresh after save
            }}
            licenseToEdit={editingLicense}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerDetails;
